import { Transition } from "@headlessui/react";
import { ArrowDownTrayIcon, DocumentTextIcon, TrashIcon } from "@heroicons/react/24/outline";
import { bytesToMB, fileTypeToColor } from "@utils/utils";
import { format } from "date-fns";
import { useState } from "react";

import { Small, Tooltip } from "@fronterahealth/frontera-ui-components";

import { LearnerFileType } from "@api/graphql/types-and-hooks";

interface AssessmentFileProps {
  file: LearnerFileType;
  downloadCallback?: (fileId: string) => void | undefined;
  removeCallback?: (fileId: string) => void | undefined;
  fileClickedCallback?: (learnerFile: LearnerFileType) => void | undefined;
}

export const AssessmentFile: React.FC<AssessmentFileProps> = ({
  file,
  downloadCallback,
  removeCallback,
  fileClickedCallback,
}) => {
  const fileNameArr: string[] = file.originalFileName.split(".");
  const fileName: string = fileNameArr[0];
  const fileType: string = fileNameArr.length > 1 ? fileNameArr[1].toUpperCase() : "";

  const fileSize: string = bytesToMB(file.fileSize, " MB");
  const fileDateDisplay: string = format(file.createdDate, "MMM d, yyyy");

  const [hovered, setHovered] = useState<boolean>(false);

  const isPdf = file.mimeType === "application/pdf";
  const canPreview = isPdf && !!fileClickedCallback;
  const showPreviewTooltip = !isPdf && !!fileClickedCallback;

  const downloadClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    downloadCallback && downloadCallback(file.id);
  };

  const removeClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    removeCallback && removeCallback(file.id);
  };

  const handleFileClick = () => {
    if (canPreview) {
      fileClickedCallback(file);
    }
  };

  const fileContent = (
    <div
      className={`bg-white rounded-sm p-2 border-limestone-100 ${canPreview ? "cursor-pointer hover:border-limestone-300" : ""} border flex items-center justify-between gap-2 w-full max-w-full grow-0`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleFileClick}
    >
      <div className="flex items-center gap-2 truncate max-w-full">
        <div className={`flex items-center justify-center rounded-xs p-1 ${fileTypeToColor(file.mimeType).background}`}>
          <DocumentTextIcon className={`w-4 h-4 ${fileTypeToColor(file.mimeType).text}`} />
        </div>
        <div className="flex flex-col truncate">
          <Small className="text-xs truncate ph-no-capture" colorType="primary">
            {fileName}
          </Small>
          <div className="flex items-center gap-2 truncate">
            {fileType && (
              <Small className="text-xs" colorType="secondary">
                {fileType}
              </Small>
            )}
            {fileType && <div className="rounded-full bg-limestone-600 w-0.5 h-0.5" />}
            <Small className="text-xs" colorType="secondary">
              {fileSize}
            </Small>
            <div className="rounded-full bg-limestone-600 w-0.5 h-0.5" />
            <Small className="text-xs truncate" colorType="secondary">
              {fileDateDisplay}
            </Small>
          </div>
        </div>
      </div>
      {(downloadCallback || removeCallback) && (
        <Transition
          show={hovered}
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="flex items-center gap-2">
            {downloadCallback && (
              <button
                className="border border-limestone-100 rounded-sm p-1 hover:bg-red-50"
                aria-label="Download File"
                onClick={downloadClicked}
              >
                <ArrowDownTrayIcon className="w-4 h-4 text-limestone-700" />
              </button>
            )}
            {removeCallback && (
              <button
                className="border border-limestone-100 rounded-sm p-1 hover:bg-red-50"
                aria-label="Remove File"
                onClick={removeClicked}
              >
                <TrashIcon className="w-4 h-4 text-red-700" />
              </button>
            )}
          </div>
        </Transition>
      )}
    </div>
  );

  if (showPreviewTooltip) {
    return (
      <Tooltip text="This file type cannot be previewed" placement="bottom" showInfoIcon={false} className="text-xs">
        {fileContent}
      </Tooltip>
    );
  }

  return fileContent;
};
